/**
 * Cart Drawer URL Handler
 *
 * Handles automatic cart drawer opening from URL parameters
 * Used for Instagram links and external product sharing
 *
 * Usage: Add ?product_id_cart_drawer=VARIANT_ID to any URL
 * Example: https://innerglowvitamins.com?product_id_cart_drawer=123456789
 * 
 * Optional parameters:
 * - selling_plan_id: The selling plan ID for subscriptions
 * - promo_code: Promo code to auto-apply
 */

(function() {
  'use strict';

  // Configuration - must match theme settings
  var VALID_PROMO_CODE = null; // Will be set from theme
  var PROMO_GIFT_MESSAGE = null; // Will be set from theme

  // Auto-open cart drawer from URL parameter (for Instagram links)
  document.addEventListener('DOMContentLoaded', function() {
    // 1. Detect URL parameters
    const urlParams = new URLSearchParams(window.location.search);
    const productIdCartDrawer = urlParams.get('product_id_cart_drawer');
    const sellingPlanId = urlParams.get('selling_plan_id'); // Optional
    const promoCode = urlParams.get('promo_code'); // Optional promo code

    // Get promo config from theme (set via Liquid in cart-drawer.liquid)
    const promoConfig = document.getElementById('promo-url-config');
    if (promoConfig) {
      VALID_PROMO_CODE = promoConfig.dataset.validCode || null;
      PROMO_GIFT_MESSAGE = promoConfig.dataset.giftMessage || null;
    }

    if (productIdCartDrawer) {
      // First, fetch current cart to check if product already exists
      fetch('/cart.js')
        .then(response => response.json())
        .then(cart => {
          // Check if product with same variant and selling plan already exists
          const existingItem = cart.items.find(item => {
            const sameVariant = item.variant_id.toString() === productIdCartDrawer.toString();
            
            // If no selling plan specified, just check variant
            if (!sellingPlanId) {
              return sameVariant && !item.selling_plan_allocation;
            }
            
            // Check if same variant AND same selling plan
            if (item.selling_plan_allocation) {
              return sameVariant && 
                     item.selling_plan_allocation.selling_plan.id.toString() === sellingPlanId.toString();
            }
            
            return false;
          });

          if (existingItem) {
            console.log('[CartDrawerURL] Product already in cart with same selling plan, skipping add');
            // Product already exists, apply promo if needed and open drawer
            return applyPromoCodeDirectlyThenOpenDrawer(promoCode, existingItem.key);
          }

          // 2. Add product to cart using AJAX API
          const cartData = {
            id: productIdCartDrawer,
            quantity: 1
          };

          // Add selling plan if provided
          if (sellingPlanId) {
            cartData.selling_plan = sellingPlanId;
          }

          return fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(cartData)
          })
          .then(response => response.json())
          .then(addedItem => {
            // Dispatch cart init event
            document.dispatchEvent(new CustomEvent('theme:cart:init', {bubbles: true}));
            
            // Apply promo and open drawer with the new item's key
            return applyPromoCodeDirectlyThenOpenDrawer(promoCode, addedItem.key);
          });
        })
        .then(() => {
          cleanUrl();
        })
        .catch((error) => {
          console.error('[CartDrawerURL] Error:', error);
          // Still try to open drawer on error
          openCartDrawer();
          cleanUrl();
        });
    }
  });

  /**
   * Applies promo code directly via API (without reload) then opens cart drawer
   */
  function applyPromoCodeDirectlyThenOpenDrawer(promoCode, lineItemKey) {
    // If no promo code provided, just open drawer
    if (!promoCode) {
      openCartDrawer();
      return Promise.resolve();
    }

    // Validate promo code against theme setting
    if (VALID_PROMO_CODE && promoCode.trim() === VALID_PROMO_CODE) {
      console.log('[CartDrawerURL] Valid promo code, applying directly...');
      
      // Step 1: Save PromoCode as cart attribute
      return fetch('/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          attributes: { PromoCode: promoCode.trim() }
        })
      })
      .then(() => {
        // Step 2: Add PromoGift as line item property
        if (PROMO_GIFT_MESSAGE && lineItemKey) {
          return fetch('/cart.js')
            .then(r => r.json())
            .then(cart => {
              const item = cart.items.find(i => i.key === lineItemKey);
              if (item) {
                const newProps = item.properties || {};
                newProps['PromoGift'] = PROMO_GIFT_MESSAGE;
                
                return fetch('/cart/change.js', {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({
                    id: lineItemKey,
                    properties: newProps
                  })
                });
              }
            });
        }
      })
      .then(() => {
        console.log('[CartDrawerURL] Promo code applied successfully');
        // Dispatch cart reload event to update drawer content
        document.dispatchEvent(new CustomEvent('theme:cart:reload', {bubbles: true}));
        
        // Wait a bit for cart to update, then open drawer
        setTimeout(() => {
          openCartDrawer();
        }, 300);
      });
    } else {
      console.log('[CartDrawerURL] Invalid promo code or no validation configured');
      openCartDrawer();
      return Promise.resolve();
    }
  }

  /**
   * Opens the cart drawer
   */
  function openCartDrawer() {
    setTimeout(function() {
      const cartDrawerElement = document.querySelector('[data-drawer="drawer-cart"]');
      const cartToggleButton = document.querySelector('[data-drawer-toggle="drawer-cart"]');

      if (cartDrawerElement) {
        cartDrawerElement.dispatchEvent(new CustomEvent('theme:drawer:open', {
          bubbles: false,
        }));
      } else if (cartToggleButton) {
        cartToggleButton.click();
      }

      // Show promo block (it should now show "Promo applied")
      setTimeout(function() {
        const promoBlock = document.getElementById('DrawerPromoBlock');
        if (promoBlock) {
          promoBlock.style.display = 'none'; // Hide since promo is already applied
        }
      }, 500);
    }, 300);
  }

  /**
   * Cleans the URL by removing cart drawer parameters
   */
  function cleanUrl() {
    const newUrl = new URL(window.location);
    newUrl.searchParams.delete('product_id_cart_drawer');
    newUrl.searchParams.delete('selling_plan_id');
    newUrl.searchParams.delete('promo_code');
    window.history.replaceState({}, '', newUrl);
  }

})();
